import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import API_BASE from "../../config";
import SectionRoster from "./SectionRoster";
import ScheduleGrid from "./ScheduleGrid";
import ClassOfferingDetailModal from "./ClassOfferingDetailModal";
import { syLabel } from "../../utils/schoolYear";
import s from "./SectionDetailPage.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/**
 * Standalone "section hub" page reachable at /academics/sections/:id.
 * Spacious counterpart to the SectionRosterModal / GroupDetailModal peeks
 * — a place to actually *manage* a section: see the roster, see the
 * subject offerings + teachers, and (in future passes) edit schedule.
 *
 * Read-only in this pass; mutations still flow through the existing
 * Directory > Enrollments and Class Offerings tabs via the action
 * buttons in the hero.
 */
export default function SectionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sectionId = id ? Number(id) : null;

  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");

  // Wide net of fetches — same pattern as ClassOfferingsPanel so we can
  // render the section in full context (grade, level, adviser, term).
  // "Level" is a TEACHING-tagged department (was a Faculty pre-merge).
  const [section,       setSection]       = useState(null);
  const [gradeLevels,   setGradeLevels]   = useState([]);
  const [teachingDepts, setTeachingDepts] = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [semesters,   setSemesters]   = useState([]);
  const [subjects,    setSubjects]    = useState([]);
  const [offerings,   setOfferings]   = useState([]);
  const [students,    setStudents]    = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [rooms,       setRooms]       = useState([]);

  const [activeTab, setActiveTab] = useState("roster"); // "roster" | "offerings" | "schedule"
  const [activeOffering, setActiveOffering] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    Promise.all([
      fetch(`${API_BASE}/api/sections/${sectionId}`).then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/departments?staffType=TEACHING`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/enrollments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
    ]).then(([sec, gl, depts, emp, sy, sem, subj, off, stu, enr, rms]) => {
      if (cancelled) return;
      setSection(sec || null);
      setGradeLevels(gl || []);
      setTeachingDepts(depts || []);
      setEmployees(emp || []);
      setSchoolYears(sy || []);
      setSemesters(sem || []);
      setSubjects(subj || []);
      setOfferings(off || []);
      setStudents(stu || []);
      setEnrollments(enr || []);
      setRooms(rms || []);
    }).catch(e => {
      if (cancelled) return;
      setErr(e.message || "Failed to load section");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [sectionId]);

  // ── Derived context ─────────────────────────────────────────────────
  const grade   = useMemo(() => (section?.gradeLevelId ? gradeLevels.find(g => g.id === section.gradeLevelId) : null), [section, gradeLevels]);
  // Level lookup follows the new departmentId key (was facultyId pre-merge).
  const level = useMemo(
    () => (grade?.departmentId ? teachingDepts.find(d => d.id === grade.departmentId) : null),
    [grade, teachingDepts]
  );
  const adviser = useMemo(() => (section?.adviserStaffId ? employees.find(e => e.id === section.adviserStaffId) : null), [section, employees]);
  const homeroom = useMemo(
    () => (section?.homeroomId ? rooms.find(r => r.id === section.homeroomId) : null),
    [section, rooms]
  );

  // Active SY is the section's own SY if available; otherwise fall back to
  // the global active SY. Active sem is the active semester within that SY.
  const sectionSy = useMemo(
    () => (section?.schoolYearId ? schoolYears.find(y => y.id === section.schoolYearId) : null),
    [section, schoolYears]
  );
  const activeSy = useMemo(
    () => sectionSy || schoolYears.find(y => y.active) || schoolYears[0] || null,
    [sectionSy, schoolYears]
  );
  const activeSem = useMemo(() => {
    if (!activeSy) return null;
    const inSy = semesters.filter(sm => String(sm.schoolYearId) === String(activeSy.id));
    return inSy.find(sm => sm.active) || inSy[0] || null;
  }, [activeSy, semesters]);

  // Class offerings for THIS section in the active term.
  const offeringsForSection = useMemo(() => {
    return (offerings || []).filter(o => {
      if (String(o.sectionId) !== String(sectionId)) return false;
      if (activeSem && String(o.semesterId) !== String(activeSem.id)) return false;
      return true;
    });
  }, [offerings, sectionId, activeSem]);

  // Stat-card values.
  const studentCount = useMemo(() => {
    const seen = new Set();
    for (const e of (enrollments || [])) {
      if (String(e.sectionId) !== String(sectionId)) continue;
      if (String(e.status || "").toUpperCase() !== "ENROLLED") continue;
      if (activeSy  && String(e.schoolYearId) !== String(activeSy.id))  continue;
      if (activeSem && String(e.semesterId)   !== String(activeSem.id)) continue;
      seen.add(e.studentId);
    }
    return seen.size;
  }, [enrollments, sectionId, activeSy, activeSem]);

  const subjectCount = useMemo(
    () => new Set(offeringsForSection.map(o => o.subjectId)).size,
    [offeringsForSection]
  );
  const teacherCount = useMemo(
    () => new Set(offeringsForSection.map(o => o.teacherStaffId).filter(x => x != null)).size,
    [offeringsForSection]
  );

  // ── Handlers ────────────────────────────────────────────────────────
  function gotoDirectoryRoster() {
    try {
      sessionStorage.setItem("directory.deeplink", JSON.stringify({
        tab: "enrollments",
        sectionId,
        schoolYearId: activeSy?.id ?? null,
        semesterId:   activeSem?.id ?? null,
      }));
    } catch { /* sessionStorage unavailable */ }
    navigate("/directory");
  }
  function gotoOfferingsTab() {
    // Class Offerings page doesn't currently consume a deep-link, so we
    // just route there — admins can pick up from the existing filters.
    navigate("/academics");
  }

  // ── Render ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.loading}>Loading section…</div>
      </div>
    );
  }
  if (err || !section) {
    return (
      <div className={s.page}>
        <div className={s.crumbs}>
          <Link to="/academics" className={s.crumbLink}>Academics</Link>
          <span className={s.crumbSep}>›</span>
          <span className={s.crumbCurrent}>Section not found</span>
        </div>
        <div className={s.empty}>
          <div className={s.emptyTitle}>Section not found</div>
          <div className={s.emptySub}>
            {err || "This section may have been deleted, or you may not have access to it."}
          </div>
        </div>
      </div>
    );
  }

  const title = grade ? `${grade.name} · ${section.name}` : section.name;

  return (
    <div className={s.page}>
      <div className={s.crumbs}>
        <Link to="/academics" className={s.crumbLink}>Academics</Link>
        <span className={s.crumbSep}>›</span>
        <Link to="/academics" className={s.crumbLink}>Sections</Link>
        <span className={s.crumbSep}>›</span>
        <span className={s.crumbCurrent}>{title}</span>
      </div>

      <header className={s.hero}>
        <div
          className={s.heroAvatar}
          style={{ background: avatarColor(section.name) }}
          aria-hidden="true">
          {initials(section.name)}
        </div>
        <div className={s.heroMain}>
          <div className={s.eyebrow}>Academics · Sections</div>
          <h1 className={s.title}>{title}</h1>
          <div className={s.metaRow}>
            {level && <span className={s.metaPill}>{level.name}</span>}
            {homeroom && (
              <span className={s.metaPill} title="Homeroom">
                {homeroom.name}
                {homeroom.building ? ` · ${homeroom.building}` : ""}
              </span>
            )}
            {adviser && (
              <span className={s.metaAdviser}>
                Adviser <b>{adviser.name}</b>
              </span>
            )}
            {(activeSy || activeSem) && (
              <>
                <span className={s.metaSep}>·</span>
                <span className={`${s.metaPill} ${s.metaPillAccent}`}>
                  {activeSy?.label ? syLabel(activeSy.label) : ""}
                  {activeSy?.label && activeSem?.label ? " · " : ""}
                  {activeSem?.label || ""}
                </span>
              </>
            )}
          </div>
        </div>
        <div className={s.heroActions}>
          <button type="button" className={s.btnGhost} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <button type="button" className={s.btnPrimary} onClick={gotoDirectoryRoster}>
            Manage Sections →
          </button>
        </div>
      </header>

      <div className={s.stats}>
        <div className={s.stat} style={{ animationDelay: "0ms" }}>
          <div className={`${s.statIcon} ${s.statIconGreen}`} aria-hidden="true">●</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{studentCount}</div>
            <div className={s.statLabel}>{studentCount === 1 ? "Student enrolled" : "Students enrolled"}</div>
          </div>
        </div>
        <div className={s.stat} style={{ animationDelay: "60ms" }}>
          <div className={`${s.statIcon} ${s.statIconAmber}`} aria-hidden="true">◇</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{subjectCount}</div>
            <div className={s.statLabel}>{subjectCount === 1 ? "Subject offered" : "Subjects offered"}</div>
          </div>
        </div>
        <div className={s.stat} style={{ animationDelay: "120ms" }}>
          <div className={`${s.statIcon} ${s.statIconRose}`} aria-hidden="true">★</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{teacherCount}</div>
            <div className={s.statLabel}>{teacherCount === 1 ? "Teacher assigned" : "Teachers assigned"}</div>
          </div>
        </div>
      </div>

      <div className={s.tabBar} role="tablist" aria-label="Section views">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "roster"}
          className={`${s.tab} ${activeTab === "roster" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("roster")}>
          Students
          <span className={s.tabCount}>{studentCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "offerings"}
          className={`${s.tab} ${activeTab === "offerings" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("offerings")}>
          Class Offerings
          <span className={s.tabCount}>{offeringsForSection.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "schedule"}
          className={`${s.tab} ${activeTab === "schedule" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("schedule")}>
          Schedule
        </button>
      </div>

      {activeTab === "roster" && (
        <div className={s.panel} role="tabpanel" aria-label="Students">
          <div className={s.panelHeader}>
            <h2 className={s.panelTitle}>Students</h2>
            <span className={s.panelSub}>
              {activeSy?.label ? syLabel(activeSy.label) : ""}
              {activeSy?.label && activeSem?.label ? " · " : ""}
              {activeSem?.label || ""}
            </span>
          </div>
          <SectionRoster
            sectionId={sectionId}
            schoolYearId={activeSy?.id ?? null}
            semesterId={activeSem?.id ?? null}
            students={students}
            enrollments={enrollments}
            sections={[].concat(section ? [section] : [])}
            gradeLevels={gradeLevels}
            schoolYears={schoolYears}
            semesters={semesters}
            rooms={rooms}
            onManage={gotoDirectoryRoster}
            defaultOpen
            hideDisclosure
          />
        </div>
      )}

      {activeTab === "offerings" && (
        <div className={s.panel} role="tabpanel" aria-label="Class Offerings">
          <div className={s.panelHeader}>
            <h2 className={s.panelTitle}>Class Offerings</h2>
            <button type="button" className={s.btnGhost} onClick={gotoOfferingsTab}>
              Edit on Class Offerings page →
            </button>
          </div>
          {offeringsForSection.length === 0 ? (
            <div className={s.empty}>
              <div className={s.emptyTitle}>No classes scheduled yet</div>
              <div className={s.emptySub}>
                This section has no subject offerings for the current term.
              </div>
            </div>
          ) : (
            <ul className={s.offeringList}>
              {offeringsForSection.map((o, i) => {
                const subj = subjects.find(x => x.id === o.subjectId);
                const tea  = o.teacherStaffId ? employees.find(x => x.id === o.teacherStaffId) : null;
                const raw  = o.schedule || "";
                const idx  = raw.lastIndexOf(" ");
                const days = idx === -1 ? raw : raw.slice(0, idx);
                const time = idx === -1 ? ""  : raw.slice(idx + 1);
                const delay = Math.min(i * 28, 360);
                return (
                  <li
                    key={o.id}
                    className={s.offerRow}
                    style={{ animationDelay: `${delay}ms` }}>
                    <div className={s.offerCode}>{subj?.code || "?"}</div>
                    <div className={s.offerMain}>
                      <div className={s.offerSubject}>{subj?.title || "—"}</div>
                      <div className={`${s.offerTeacher} ${tea ? "" : s.offerTeacherWarn}`}>
                        {tea?.name || "No teacher assigned"}
                      </div>
                    </div>
                    <div className={s.offerDays}>{days || "—"}</div>
                    <div className={s.offerTime}>{time || "—"}</div>
                    <div className={s.offerRoom}>{(o.roomId != null && rooms.find(r => r.id === o.roomId)?.name) || o.room || "—"}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {activeTab === "schedule" && (
        <div className={s.panel} role="tabpanel" aria-label="Schedule">
          <div className={s.panelHeader}>
            <h2 className={s.panelTitle}>Schedule</h2>
            <span className={s.panelSub}>
              Click any class to see subject, teacher, and students.
            </span>
          </div>
          <ScheduleGrid
            offerings={offeringsForSection}
            subjects={subjects}
            sections={section ? [section] : []}
            employees={employees}
            rooms={rooms}
            gradeLevels={gradeLevels}
            colorBy="subject"
            primaryFor="section"
            onBlockClick={setActiveOffering}
          />
        </div>
      )}

      <ClassOfferingDetailModal
        open={!!activeOffering}
        offering={activeOffering}
        subjects={subjects}
        sections={section ? [section] : []}
        gradeLevels={gradeLevels}
        employees={employees}
        schoolYears={schoolYears}
        semesters={semesters}
        rooms={rooms}
        offerings={offerings}
        students={students}
        enrollments={enrollments}
        onClose={() => setActiveOffering(null)}
      />
    </div>
  );
}
